// components
import { DatePicker, Select, Space } from "antd";
import Button from "components/atoms/Button/Button";
import getIcon, { IconProps } from "utils/getIcon";
import { getToYearList } from "utils/getYearList";
import StepContainer from "components/molecules/StepContainer/StepContainer";
import { Head } from "components/molecules/Head/Head";
import { DropdownItem, useReportGenerationAdmin } from "components/organisms/ReportGenerationAdmin/useReportGenerationAdmin";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import Spinner from "components/atoms/Spinner/Spinner";

// styles
import styles from "components/organisms/ReportGenerationAdmin/ReportGenerationAdmin.module.css";
import theme from "styles/theme";

// Constants
import { ReportGeneration as Constants, PageTitles, getYearList, DateTimeFormats } from "constants/global";

// hooks
import { OutletItem } from "services/api/types";
import dayjs from "dayjs";

const { Option } = Select;
const Icon = (props: IconProps) => getIcon({ ...props });

export type ReportGenerationTenantProps = {
	outlets: Array<OutletItem>;
};

type ReportType = {
	type: string | null;
	selected: boolean;
};

type VoucherType = {
	type: string | null;
	selected: boolean;
};

type ReportGenerationTenantPageProps = {
	reportType: ReportType;
	terminal: string | null;
	voucherType: VoucherType;
	fromMonth: string | null;
	fromYear: number | null;
	toMonth: string | null;
	toYear: number | null;
	fromError?: boolean;
	toError?: boolean;
	loading: boolean;
	tenantName: { name: string; input: boolean };
	outletName: { name: string; input: boolean };
	voucherTypesList: DropdownItem[];
	terminalsList: DropdownItem[];
	tenantsList: DropdownItem[];
	outletsList: OutletItem[];
	tenantSearchInput: string | null;
	outletSearchSelected: boolean;
	tenantSearchSelected: boolean;
	voucherRedemptionStartDate: string;
	voucherRedemptionEndDate: string;
	dateRangeError: boolean;
	handleTerminalType: (value: string) => void;
	handleTenantName: (value: string) => void;
	handleOutletName: (value: string) => void;
	handleFromMonth: (value: string) => void;
	handleFromYear: (value: number) => void;
	handleToMonth: (value: string) => void;
	handleToYear: (value: number) => void;
	handleReportType: (value: string) => void;
	handleVoucherType: (value: string) => void;
	isDownloadButtonEnabled: () => boolean;
	proceedToGenerateReport: () => void;
	onTenantSearch: (value: string) => void;
	onOutletSearch: (value: string) => void;
	handleVoucherRedemptionDate: (value: string, value2: string) => void;
};

type ErrorMessageWrapperTypes = {
	fieldError?: boolean;
	message: string;
};

type DateErrorClasses = {
	fromYear: number | null;
	fromMonth: string | null;
	toYear: number | null;
	toMonth: string | null;
	fromError: boolean | undefined;
	toError: boolean | undefined;
	dateRangeError: boolean;
};

const ErrorMessageWrapper = ({ fieldError, message }: ErrorMessageWrapperTypes) => {
	return fieldError ? <p className={styles?.error_style}>{message}</p> : null;
};

function getErrorClasses({ fromYear, fromMonth, toYear, toMonth, fromError, toError, dateRangeError }: DateErrorClasses) {
	return {
		fromDateCls: fromYear && fromMonth ? styles?.filled : "",
		toDateCls: toYear && toMonth ? styles?.filled : "",
		isFromDateSelected: fromMonth && fromYear,
		fromDateError: fromError ? styles.error : "",
		toDateError: toError ? styles.error : "",
		dateRangeErrCls: dateRangeError ? styles.error : ""
	};
}

function showDatePicker(reportType: string) {
	return Constants.SHOW_DATEPICKERS?.includes(reportType);
}

const ReportGenerationAdmin = () => {
	const componentProps: ReportGenerationTenantPageProps = useReportGenerationAdmin();
	const {
		tenantName,
		outletName,
		reportType,
		fromMonth,
		fromYear,
		toMonth,
		toYear,
		terminal,
		voucherTypesList,
		terminalsList,
		tenantsList,
		fromError,
		loading,
		toError,
		outletsList,
		outletSearchSelected,
		tenantSearchSelected,
		voucherRedemptionStartDate,
		voucherRedemptionEndDate,
		dateRangeError,
		handleTerminalType,
		handleFromMonth,
		handleFromYear,
		handleToMonth,
		handleToYear,
		voucherType,
		handleReportType,
		handleVoucherType,
		handleTenantName,
		handleOutletName,
		isDownloadButtonEnabled,
		proceedToGenerateReport,
		onTenantSearch,
		onOutletSearch,
		handleVoucherRedemptionDate
	} = componentProps;

	const { fromDateCls, toDateCls, isFromDateSelected, fromDateError, toDateError, dateRangeErrCls } = getErrorClasses({
		fromYear,
		fromMonth,
		toYear,
		toMonth,
		fromError,
		toError,
		dateRangeError
	});

	let stepNumber = 0;

	const getOptions = (arr: DropdownItem[]) => {
		return arr?.map((item) => (
			<Option key={item?.id} value={item?.value} className={styles.option_style}>
				{item?.label || item?.value}
			</Option>
		));
	};

	const selectClassName = voucherType.selected ? styles?.active_select_input : styles?.selectInput;
	const datePickerClassName = `${styles?.date_picker} ${voucherRedemptionStartDate ? styles.filled : ""}`;

	return (
		<div className="relative">
			{loading && <Spinner type="transparent" />}
			<PageTitle title={Constants.NEW_USER_HEADER} subTitle={Constants.NEW_USER_SUBHEADER} />
			<div className={styles?.root}>
				<Head>{PageTitles.REPORT_GENERATION}</Head>
				<div className={styles?.margin}>
					<div className={styles?.step_header_wrapper}>
						<StepContainer showRequiredForSubHeader step={++stepNumber} subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_1_SUBHEADER} active={true} />
					</div>
					<div className={styles?.select_wrapper}>
						<Select
							data-testid={Constants.REPORT_TYPE_LABEL}
							placeholder={Constants.REPORT_TYPE_LABEL}
							value={reportType.type}
							className={reportType.selected ? styles?.active_select_input : styles?.selectInput}
							onChange={handleReportType}
							bordered={false}
						>
							{getOptions(Constants.ADMIN_REPORT_TYPE)}
						</Select>
					</div>
				</div>
				<div className={styles?.step_wrapper}>
					{reportType?.type !== Constants.ADMIN_REPORT_TYPE[3].value ? (
						<>
							<div className={styles?.step_header_wrapper}>
								<StepContainer step={++stepNumber} subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_2_SUBHEADER} active={reportType.selected} />
							</div>
							<div className={styles?.select_wrapper}>
								<Select
									data-testid={Constants.VOUCHER_TYPE_LABEL}
									placeholder={Constants.VOUCHER_TYPE_LABEL}
									value={voucherType.type}
									className={selectClassName}
									onChange={handleVoucherType}
									bordered={false}
									disabled={!reportType.selected}
								>
									{getOptions(voucherTypesList)}
								</Select>
							</div>
						</>
					) : null}
				</div>
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_header_wrapper}>
						<StepContainer step={++stepNumber} subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_3_SUBHEADER} active={reportType?.selected} />
					</div>
					<div className={styles?.select_wrapper}>
						<Select
							data-testid={Constants.TERMINAL}
							placeholder={Constants.TERMINAL}
							value={terminal}
							className={terminal ? styles?.active_select_input : styles?.selectInput}
							onChange={handleTerminalType}
							bordered={false}
							disabled={!reportType.selected}
						>
							{getOptions(terminalsList)}
						</Select>
					</div>
				</div>
				<div className={styles?.step_wrapper}>
					<div className={styles?.step_header_wrapper}>
						<StepContainer step={++stepNumber} subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_4_SUBHEADER} active={reportType?.selected} />
					</div>
					<div className={styles?.select_wrapper}>
						<Select
							showSearch
							className={`left-nav-search ${styles.inputSearch} ${tenantName?.name ? styles?.filled : ""}`}
							optionFilterProp="children"
							placeholder={Constants.REPORT_GENERATION_ADMIN.TENENT_NAME_EX}
							onChange={handleTenantName}
							onSearch={onTenantSearch}
							value={!tenantName?.name ? null : tenantName?.name}
							data-testid="tenant_search"
							listHeight={325}
							disabled={outletSearchSelected || !reportType.selected}
							dropdownStyle={theme.dropdownStyle}
							suffixIcon={<span className="cursor-pointer">{getIcon({ icon: "search" })}</span>}
							filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())}
						>
							{tenantsList?.map((outlet: any) => {
								return (
									<Option value={`${outlet.id}`} key={`${outlet.id}`} className="flex-col px-2 py-2">
										{`${outlet.label} ${outlet.id}`}
									</Option>
								);
							})}
						</Select>
						<span className={styles?.ortxt}>or</span>
						<Select
							showSearch
							className={`left-nav-search ${styles.inputSearch} ${outletName?.name ? styles?.filled : ""}`}
							optionFilterProp="children"
							placeholder={Constants.REPORT_GENERATION_ADMIN.OUTLET_NAME_EX}
							onChange={handleOutletName}
							onSearch={onOutletSearch}
							value={!outletName?.name ? null : outletName?.name}
							data-testid="outlet_search"
							listHeight={325}
							disabled={tenantSearchSelected || !reportType.selected}
							dropdownStyle={theme.dropdownStyle}
							suffixIcon={<span className="cursor-pointer">{getIcon({ icon: "search" })}</span>}
							filterOption={(input, option) => (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())}
						>
							{outletsList?.map((outlet: OutletItem) => {
								return (
									<Option value={`${outlet.outlet_code}`} key={`${outlet.outlet_code}`} className="flex-col px-2 py-2">
										{`${outlet.outlet_name} ${outlet.outlet_code}`}
									</Option>
								);
							})}
						</Select>
					</div>
				</div>
				<div className={styles?.step_wrapper}>
					{showDatePicker(`${reportType?.type}`) ? (
						<>
							<div className={styles?.step_header_wrapper}>
								<StepContainer
									step={++stepNumber}
									showRequiredForSubHeader
									subHeader={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_4_SUBHEADER_REDEMPTION}
									active={reportType?.selected}
								/>
							</div>
							<Space direction="vertical" size={24}>
								<DatePicker
									data-testid={Constants.DATE_PLACEHOLDER}
									disabled={!reportType?.selected}
									allowClear={false}
									className={datePickerClassName}
									placeholder={Constants.DATE_PLACEHOLDER}
									onChange={(_, dateString) => {
										handleVoucherRedemptionDate(Constants.DATE_TYPE.START_DATE, dateString);
									}}
									suffixIcon={getIcon({ icon: "calender" })}
									format={DateTimeFormats.USE_YYYY_MM_DD}
									disabledDate={(current) => {
										return current && dayjs(current as any).isAfter(dayjs().subtract(1, "day"), "day");
									}}
								/>
							</Space>
						</>
					) : (
						<>
							<div className={styles?.step_header_wrapper}>
								<StepContainer step={++stepNumber} showRequiredForSubHeader subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_5_SUBHEADER} active={reportType?.selected} />
							</div>

							<div className={styles.calender_select_wrapper}>
								<Select
									data-testid={Constants.SELECT_MONTH}
									className={`${styles?.custom_month} ${fromDateCls} ${fromDateError}`}
									placeholder={Constants.SELECT_MONTH}
									showArrow={false}
									disabled={!reportType.selected}
									bordered={false}
									value={fromMonth}
									onChange={handleFromMonth}
								>
									{getOptions(Constants.MONTH)}
								</Select>
								<div className={styles?.break_point}>
									<br />
								</div>
								<Select
									data-testid={Constants.SELECT_YEAR}
									className={`${styles?.custom_year} ${fromDateCls} ${fromDateError}`}
									placeholder={Constants.SELECT_YEAR}
									showArrow={true}
									disabled={!reportType.selected}
									suffixIcon={<Icon icon="calender" />}
									bordered={false}
									value={fromYear}
									onChange={handleFromYear}
								>
									{getOptions(getYearList())}
								</Select>
							</div>
							{fromError && <p className={styles?.error_style}>{Constants.CANNOT_GENERATE_REPORT}</p>}
						</>
					)}
				</div>
				<div className={"mt-[1.25rem] w-full"}>
					{showDatePicker(`${reportType?.type}`) ? (
						<>
							<div className={styles?.step_header_wrapper}>
								<StepContainer step={++stepNumber} subHeader={Constants.REPORT_GENERATION_TENANT_WITH_MULTIPLE_OUTLETS.STEP_5_SUBHEADER_REDEMPTION} active={reportType?.selected} />
							</div>
							<Space direction="vertical" size={24}>
								<DatePicker
									data-testid={Constants.DATE_TYPE.END_DATE}
									disabled={!reportType?.selected || !voucherRedemptionStartDate}
									allowClear={false}
									className={`${styles?.date_picker} ${voucherRedemptionEndDate && !dateRangeError ? styles.filled : dateRangeErrCls}`}
									placeholder={Constants.DATE_PLACEHOLDER}
									onChange={(_, dateString) => {
										handleVoucherRedemptionDate(Constants.DATE_TYPE.END_DATE, dateString);
									}}
									suffixIcon={getIcon({ icon: "calender" })}
									format={DateTimeFormats.USE_YYYY_MM_DD}
									disabledDate={(current) => {
										return current && dayjs(current as any).isBefore(dayjs(voucherRedemptionStartDate), "day");
									}}
								/>
							</Space>
							<ErrorMessageWrapper message={Constants.DATE_RANGE_ERROR} fieldError={dateRangeError} />
						</>
					) : (
						<>
							<div className={styles?.step_header_wrapper}>
								<StepContainer
									step={stepNumber + 1}
									optional={true}
									noMaxWidth={true}
									subHeader={Constants.REPORT_GENERATION_ADMIN.STEP_6_SUBHEADER}
									active={!!isFromDateSelected}
								/>
							</div>

							<div className={styles.calender_select_wrapper}>
								<Select
									data-testid={`TO_${Constants.SELECT_MONTH}`}
									className={`${styles?.custom_month} ${toDateCls} ${toDateError}`}
									placeholder={Constants.SELECT_MONTH}
									showArrow={false}
									bordered={false}
									disabled={!isFromDateSelected}
									value={toMonth}
									onChange={handleToMonth}
								>
									{getOptions(Constants.MONTH)}
								</Select>
								<div className={styles?.break_point}>
									<br />
								</div>
								<Select
									data-testid={`TO_${Constants.SELECT_YEAR}`}
									className={`${styles?.custom_year} ${toDateCls} ${toDateError}`}
									placeholder={Constants.SELECT_YEAR}
									showArrow={true}
									disabled={!isFromDateSelected}
									suffixIcon={<Icon icon="calender" />}
									bordered={false}
									value={toYear}
									onChange={handleToYear}
								>
									{getOptions(getToYearList(fromYear))}
								</Select>
							</div>
							{toError && <p className={styles?.error_style}>{Constants.CANNOT_GENERATE_REPORT}</p>}
						</>
					)}
				</div>

				<div className={styles?.btn_download_wrapper}>
					<Button onClick={proceedToGenerateReport} label={Constants.DOWNLOAD_REPORT} type={isDownloadButtonEnabled() ? "activeLong" : "disabledLong"} />
				</div>
			</div>
		</div>
	);
};

export default ReportGenerationAdmin;
